import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import { getUserInfoFromProfile } from 'site-modules/shared/components/lead-form/utils/utils';
import {
  LeadFormCommonPropTypes,
  LeadFormCommonDefaultProps,
  UnifiedLeadFormCustomConfigPropTypes,
  UnifiedLeadFormCustomConfigDefaultProps,
} from 'site-modules/shared/components/lead-form/lead-form-prop-types/lead-form-prop-types';
import { FormViewDecorator } from 'site-modules/shared/components/lead-form/unified-lead-form/views/form-view-decorator';
import FormGroup from 'reactstrap/lib/FormGroup';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import Button from 'reactstrap/lib/Button';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { PHONE_MASK_V2 } from 'site-modules/shared/constants/lead-form/validation';
import { ContactDealerTip } from 'client/site-modules/shared/components/inventory/contact-dealer-tip/contact-dealer-tip';
import { PrivacyNote } from 'site-modules/shared/components/lead-form/unified-lead-form/components/privacy-note/privacy-note';
import {
  getAskForPriceCopy,
  getUrgencyMessage,
} from 'client/site-modules/shared/utils/inventory-utils/get-urgency-message';
import { isCarMax, isEchoParkAutomotive, isHerbChambers } from 'site-modules/shared/utils/dealer-details-utils';
import { VdpEmbeddedHeader } from 'site-modules/shared/components/lead-form/unified-lead-form/components/header/vdp-embedded-header';
import { LoadingDots } from 'site-modules/shared/components/loading-dots/loading-dots';
import { WithZipLongDistanceConfirmView } from 'site-modules/shared/components/lead-form/unified-lead-form/views/form-view/with-zip-long-distance-confirm-view/with-zip-long-distance-confirm-view';
import { ErrorFieldCopy } from 'site-modules/shared/components/lead-form/error-field-copy/error-field-copy';
import {
  DISCLAIMER_TYPES,
  withDynamicDisclaimer,
} from 'site-modules/shared/components/lead-form/utils/with-dynamic-disclaimer';

import './top-embedded-form-view.scss';

const fieldClassName = 'w-100 mb-0';
const fieldLabelClassName = 'mb-0 pos-a top-0 left-0 mt-0_5 ms-1 small';
const fieldErrorClassName = 'xsmall text-danger';

const FORM_LABELS_LEAD_FORM = {
  nameLabel: 'First Name',
  lastNameLabel: 'Last Name',
  emailLabel: 'Email Address',
  phoneNumLabel: 'Phone Number',
};

const FORM_LABELS_MOBILE = {
  nameLabel: 'First name',
  lastNameLabel: 'Last name',
  emailLabel: 'Email address',
  phoneNumLabel: 'Phone number',
};

function getLabelsPlaceholders(isMobile) {
  return isMobile ? FORM_LABELS_MOBILE : FORM_LABELS_LEAD_FORM;
}

class TopEmbeddedFormViewUI extends Component {
  static propTypes = {
    ...LeadFormCommonPropTypes,
    customConfig: UnifiedLeadFormCustomConfigPropTypes.leadFormConfig,
    profile: PropTypes.shape({}),
    templateParams: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    disclaimerType: PropTypes.oneOf([DISCLAIMER_TYPES.BASIC, DISCLAIMER_TYPES.SPECIFIC]),
  };

  static defaultProps = {
    ...LeadFormCommonDefaultProps,
    customConfig: {
      ...UnifiedLeadFormCustomConfigDefaultProps.leadFormConfig,
    },
    profile: {},
    templateParams: [],
    disclaimerType: DISCLAIMER_TYPES.BASIC,
  };

  render() {
    const {
      fieldRef,
      validationErrors,
      validate,
      customConfig: {
        submitButtonText,
        daysToSell,
        tcpaParentDealershipName,
        isMobile,
        hideContactDealerTip,
        buyonline,
        dealerName,
        submitButtonColor,
        headerConfig,
        creativeId,
        isLoading,
      },
      profile,
      showSubmitLoadingIndicator,
      resetFieldValidation,
      leadTargets,
      zipCode,
      templateParams,
      make,
      model,
      disclaimerType,
    } = this.props;

    const firstNameValid = !('firstName' in validationErrors);
    const lastNameValid = !('lastName' in validationErrors);
    const emailValid = !('email' in validationErrors);
    const phoneValid = !('phone' in validationErrors);

    const { firstName, lastName, email, phoneNumber } = getUserInfoFromProfile(profile);
    const formLabelsPlaceholders = getLabelsPlaceholders(isMobile);
    const askForPriceCopy = getAskForPriceCopy(
      isCarMax(dealerName) || isEchoParkAutomotive(dealerName) || isHerbChambers(dealerName)
    );
    const defaultMsg = buyonline ? `A ${dealerName} agent will answer your questions.` : askForPriceCopy;
    const tip = (buyonline || !hideContactDealerTip) && (
      <ContactDealerTip
        daysToSell={daysToSell}
        defaultMsg={defaultMsg}
        classNamesWrapper="mb-1_25 text-center"
        classNamesMsg={''}
      />
    );
    const message = getUrgencyMessage(daysToSell, defaultMsg);
    const subHeader = headerConfig?.subHeader?.text;

    return (
      <WithZipLongDistanceConfirmView
        inventory={get(leadTargets[0], 'inventory')}
        model={model}
        make={make}
        submitButtonColor={submitButtonColor}
        zipCode={zipCode}
        creativeId={creativeId}
      >
        <div className="top-embedded-form-view pos-r">
          <VdpEmbeddedHeader config={get(headerConfig, 'header')} templateParams={templateParams} />
          <Row
            className={classNames('price-summary-no-text-lead-form text-info', {
              'mt-1': !tip || !message,
              'mt-0_25': !!tip,
            })}
          >
            <Col xs={12}>
              {subHeader ? <div className="size-16 fw-normal mb-1_25 text-center">{subHeader}</div> : tip}
              <fieldset className="user-info rounded">
                <legend className="visually-hidden">Your Contact Information</legend>
                <Row className="justify-content-between align-items-start">
                  <Col xs={6} md={12} lg={6}>
                    <FormGroup className="mb-0_5 form-group" noMargin>
                      <div>
                        <FormField
                          id="lead-form-pricing-fname"
                          name="firstName"
                          label={formLabelsPlaceholders.nameLabel}
                          aria-required
                          aria-invalid={!firstNameValid}
                          aria-describedby="error-top-embedded-firstName"
                          isValid={firstNameValid}
                          labelClassName={fieldLabelClassName}
                          maxLength={60}
                          onBlur={validate}
                          onChange={resetFieldValidation}
                          defaultValue={firstName}
                          className="mb-0"
                          inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
                          resetTouchOnChange
                          ref={fieldRef}
                          autoComplete="given-name"
                        />
                        <ErrorFieldCopy
                          id="error-top-embedded-firstName"
                          className={fieldErrorClassName}
                          copy={validationErrors.firstName}
                          tag="span"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col xs={6} md={12} lg={6}>
                    <FormGroup className="mb-0_5 form-group" noMargin>
                      <div>
                        <FormField
                          id="lead-form-pricing-lname"
                          name="lastName"
                          label={formLabelsPlaceholders.lastNameLabel}
                          aria-required
                          aria-invalid={!lastNameValid}
                          aria-describedby="error-top-embedded-lastName"
                          isValid={lastNameValid}
                          labelClassName={fieldLabelClassName}
                          maxLength={60}
                          onBlur={validate}
                          onChange={resetFieldValidation}
                          defaultValue={lastName}
                          className="mb-0"
                          inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
                          resetTouchOnChange
                          ref={fieldRef}
                          autoComplete="family-name"
                        />
                        <ErrorFieldCopy
                          id="error-top-embedded-lastName"
                          className={fieldErrorClassName}
                          copy={validationErrors.lastName}
                          tag="span"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="justify-content-between align-items-start">
                  <Col xs={12} lg={6}>
                    <FormGroup className="w-100 mb-0_5 form-group" noMargin>
                      <div className="user-info-email w-100">
                        <FormField
                          id="lead-form-pricing-email"
                          name="email"
                          label={formLabelsPlaceholders.emailLabel}
                          aria-required
                          aria-invalid={!emailValid}
                          aria-describedby="error-top-embedded-email"
                          isValid={emailValid}
                          type="email"
                          className={fieldClassName}
                          labelClassName={fieldLabelClassName}
                          onBlur={validate}
                          onChange={resetFieldValidation}
                          defaultValue={email}
                          inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
                          resetTouchOnChange
                          ref={fieldRef}
                          autoComplete="email"
                        />
                        <ErrorFieldCopy
                          id="error-top-embedded-email"
                          className={fieldErrorClassName}
                          copy={validationErrors.email}
                          tag="span"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col xs={12} lg={6}>
                    <FormGroup className="w-100 mb-1 form-group" noMargin>
                      <div className="w-100">
                        <FormFieldMask
                          id="lead-form-pricing-phone"
                          name="phone"
                          label={formLabelsPlaceholders.phoneNumLabel}
                          aria-required
                          aria-invalid={!phoneValid}
                          aria-describedby="error-top-embedded-phone"
                          isValid={phoneValid}
                          type="tel"
                          mask={PHONE_MASK_V2}
                          className={fieldClassName}
                          labelClassName={fieldLabelClassName}
                          onBlur={validate}
                          onChange={resetFieldValidation}
                          defaultValue={phoneNumber}
                          inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
                          resetTouchOnChange
                          ref={fieldRef}
                          autoComplete="tel"
                        />
                        <ErrorFieldCopy
                          id="error-top-embedded-phone"
                          className={fieldErrorClassName}
                          copy={validationErrors.phone}
                          tag="span"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <div className="mb-1_5">
                  {disclaimerType === DISCLAIMER_TYPES.SPECIFIC && (
                    <PrivacyNote
                      tcpaParentDealershipName={tcpaParentDealershipName}
                      ctaText={submitButtonText}
                      isSingleSelect
                      disclaimerType={disclaimerType}
                      classNames="mb-1"
                    />
                  )}
                  <Button
                    type="submit"
                    color={submitButtonColor}
                    className={classNames('submit-button btn-lg px-1 py-0_5 fw-medium', {
                      'spinner-active': showSubmitLoadingIndicator,
                      'w-100': isMobile,
                    })}
                    style={{ width: '100%' }}
                    disabled={showSubmitLoadingIndicator}
                  >
                    <i className="spinner icon-spinner8 me-0_25" aria-hidden />
                    <span>{submitButtonText}</span>
                  </Button>
                  {disclaimerType === DISCLAIMER_TYPES.BASIC && (
                    <div className="mt-1">
                      <PrivacyNote
                        tcpaParentDealershipName={tcpaParentDealershipName}
                        ctaText={submitButtonText}
                        isSingleSelect
                        disclaimerType={disclaimerType}
                      />
                    </div>
                  )}
                </div>
              </fieldset>
            </Col>
          </Row>
          {isLoading && (
            <div className="h-100 w-100 pos-a top-0 left-0 bg-cool-gray-100 rounded-16 d-flex align-items-center justify-content-center">
              <LoadingDots dotClassName="wizard-dots bg-cool-gray-60 mx-0_25" />
            </div>
          )}
        </div>
      </WithZipLongDistanceConfirmView>
    );
  }
}

export const TopEmbeddedFormView = withDynamicDisclaimer(FormViewDecorator(TopEmbeddedFormViewUI));
